/**
 * A function to create a log object for the 'serverPushNotificationSent' event (evid: 2002)
 *
 * @param {serverPushNotificationSentParams} params - The parameters for this event
 * @return {LogObject<serverPushNotificationSentParams>} LogObject<serverPushNotificationSentParams>
 */
export function serverPushNotificationSent(params) {
    return { evid: 2002, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'ctaClick' event (evid: 2003)
 *
 * Event description: generic event for cta click on a wix page
 *
 * @param {ctaClickParams} params - The parameters for this event
 * @property {boolean} isValid - was the click valid
 * @property {string} pageUrl - url  of the page where the click happend
 * @property {string} targetUrl - target url of the click
 * @property {string} foldName - name of the fold where the cta was clicked
 * @property {boolean} isMainCta - is main cta of the page
 * @property {number} foldNumber - fold where the cta was clicked
 * @property {string} buttonName - text of the button (alt text if it's an image)
 * @property {string} pageName - self declared page name
 * @return {LogObject<ctaClickParams>} LogObject<ctaClickParams>
 */
export function ctaClick(params) {
    return { evid: 2003, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'pageScroll' event (evid: 2004)
 *
 * Event description: event fired where anonymous or user scroll through one of the wix page s
 *
 * @param {pageScrollParams} params - The parameters for this event
 * @property {boolean} isPageEnd - TRUE if this fold is the last on the page, FALSE if not
 * @property {string} pageUrl - url of the page where the event is fired
 * @property {string} foldName - name of the fold reached scrolling
 * @property {number} foldNumber - number of the fold reached scrolling
 * @property {string} pageName - self declared page name
 * @return {LogObject<pageScrollParams>} LogObject<pageScrollParams>
 */
export function pageScroll(params) {
    return { evid: 2004, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'popUpModalLoaded' event (evid: 2005)
 *
 * Event description: A modal/popup/lightbox was loaded in the page
 *
 * @param {popUpModalLoadedParams} params - The parameters for this event
 * @property {string} pageUrl - url of the page where the modal was loaded
 * @return {LogObject<popUpModalLoadedParams>} LogObject<popUpModalLoadedParams>
 */
export function popUpModalLoaded(params) {
    return { evid: 2005, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'textFieldSubmit' event (evid: 2006)
 *
 * Event description: Event is fired with the text typed in the text field when clicking the corresponding CTA.
 *
 * @param {textFieldSubmitParams} params - The parameters for this event
 * @property {string} inputText - text filled (by the user) in the text box
 * @property {string} pageUrl - url of the page where the text was submitted
 * @property {string} inputTextType - Self declared field stating what is the input required from the user (f.e. email/business name/website industry and etc')
 * @property {string} buttonName - name of the button that was clicked to submit the text in the text box
 * @return {LogObject<textFieldSubmitParams>} LogObject<textFieldSubmitParams>
 */
export function textFieldSubmit(params) {
    return { evid: 2006, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'clickOnTextBox' event (evid: 2007)
 *
 * Event description: Generic Event, fired when anon/user clicks on text box. Usually will be used for PII text boxes.
 *
 * @param {clickOnTextBoxParams} params - The parameters for this event
 * @property {string} buttonName - name of the text box clicked
 * @property {string} pageUrl - url of the page where the text was submitted
 * @return {LogObject<clickOnTextBoxParams>} LogObject<clickOnTextBoxParams>
 */
export function clickOnTextBox(params) {
    return { evid: 2007, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'customMarketingHeaderAction' event (evid: 2020)
 *
 * Event description: Fires for any user action on any custom header in a marketing page.
 *
 * @param {customMarketingHeaderActionParams} params - The parameters for this event
 * @property {string} tab - Tab under which button is located (when applicable)
 * @property {string} pageUrl - URL of page in which the action is performed
 * @property {number} hoverMs - total hover time in milliseconds
 * @property {string} targetUrl - URL where a tab click redirects the user
 * @property {string} productName - Product/product area this header is related to (i.e. Enterprise, Partners, Wix Studio…)
 * @property {string} buttonName - Text on the button clicked/hovered
 * @property {string} action - Type of action performed - click/hover/open tab/close tab
 * @return {LogObject<customMarketingHeaderActionParams>} LogObject<customMarketingHeaderActionParams>
 */
export function customMarketingHeaderAction(params) {
    return { evid: 2020, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'customMarketingFooterView' event (evid: 2021)
 *
 * Event description: Event fires every time user or visitor is exposed to a custom Marketing footer. This means that the user scrolled down and got to the footer.
 *
 * @param {customMarketingFooterViewParams} params - The parameters for this event
 * @property {string} productName - Product/product area this footer is related to (i.e. Enterprise, Partners, Wix Studio…)
 * @property {string} pageUrl - URL of page in which the footer was seen
 * @return {LogObject<customMarketingFooterViewParams>} LogObject<customMarketingFooterViewParams>
 */
export function customMarketingFooterView(params) {
    return { evid: 2021, src: 19, endpoint: 'any-marketing-lp', params: params };
}
/**
 * A function to create a log object for the 'customMarketingFooterClick' event (evid: 2022)
 *
 * Event description: Event fires every time the user clicks on a link in any custom Marketing footer.
 *
 * @param {customMarketingFooterClickParams} params - The parameters for this event
 * @property {string} pageUrl - URL of page in which the click is performed
 * @property {string} targetUrl - URL where the click redirects the user
 * @property {string} section - Name of footer section under which button is located
 * @property {string} productName - Product/product area this footer is related to (i.e. Enterprise, Partners, Wix Studio…)
 * @property {string} buttonName - Text of the clicked button / link
 * @return {LogObject<customMarketingFooterClickParams>} LogObject<customMarketingFooterClickParams>
 */
export function customMarketingFooterClick(params) {
    return { evid: 2022, src: 19, endpoint: 'any-marketing-lp', params: params };
}
//# sourceMappingURL=index.js.map